// PortalOffers — /portal/offers. The client-facing recommended applications.
//
// The client picks their lane (personal funding vs business funding — saved
// as clients.funding_preference), then sees the recommendation engine's
// output for their own file: product cards with plain-English reasons, the
// compliance warnings rendered verbatim (reports-to-personal above all),
// and the educational disclaimers. Business path may arrive gated with the
// "build first" explanation instead of cards.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldAlert, ArrowLeft } from "lucide-react";
import { PortalShell } from "../components/PortalShell";

async function api(path: string, opts: { method?: string; body?: any } = {}) {
  const res = await fetch(path, {
    method: opts.method || "GET",
    credentials: "include",
    headers: opts.body ? { "Content-Type": "application/json" } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) { window.location.href = "/portal/login"; throw new Error("unauthorized"); }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
  return data;
}

const TIER_LABEL: Record<string, string> = { strong: "Strong match", match: "Match", reach: "Stretch" };

export default function PortalOffersPage() {
  const [data, setData] = useState<any>(null);
  const [path, setPath] = useState<"personal" | "business" | "both">("both");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});

  async function load(p = path) {
    setLoading(true);
    try { setData(await api(`/api/portal/offers?path=${p}`)); }
    catch (e: any) { if (e.message !== "unauthorized") setErr(e?.message || "Could not load offers."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function choose(p: "personal" | "business" | "both") {
    setPath(p);
    load(p);
    api("/api/portal/offers", { method: "POST", body: { preference: p } }).catch(() => {});
  }

  return (
    <PortalShell actions={<Link to="/portal/dashboard" className="portal-btn-ghost"><ArrowLeft size={14} aria-hidden="true" /> Dashboard</Link>}>
      <h1 className="portal-h1" style={{ marginBottom: 4 }}>Recommended applications</h1>
      <p className="portal-sub" style={{ marginBottom: 16 }}>
        Matched to your credit profile. Pick your lane:
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {(["personal", "business", "both"] as const).map((p) => (
          <button key={p} onClick={() => choose(p)}
            className={path === p ? "portal-btn-primary" : "portal-btn-ghost"}>
            {p === "personal" ? "Personal funding" : p === "business" ? "Business funding" : "Show me both"}
          </button>
        ))}
      </div>

      {err && <p className="portal-sub" style={{ color: "var(--ne-red-dark)" }}>{err}</p>}
      {loading && <p className="portal-sub">Loading your matches…</p>}

      {!loading && data && data.enabled === false && (
        <article className="portal-card">
          <p className="portal-empty">
            Your advisor hand-picks the right applications for your file and will walk you through
            each one at the right moment — applying in the right ORDER is half the game. Reach out
            to your advisor to go over your matches.
          </p>
        </article>
      )}
      {!loading && data && data.enabled !== false && !data.analyzed && (
        <article className="portal-card">
          <p className="portal-empty">Your credit analysis isn't ready yet — your matches unlock as soon as it is.</p>
        </article>
      )}

      {!loading && (data?.results || []).map((res: any) => (
        <article className="portal-card" key={res.path} style={{ marginBottom: 16 }}>
          <div className="portal-card-heading">
            <h2>{res.path === "personal" ? "Personal funding — loans & cards" : "Business funding — business credit cards"}</h2>
          </div>

          {!res.available && (
            <p className="portal-sub on-card" style={{ fontSize: 13 }}>
              <ShieldAlert size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
              {res.gate_reason}
            </p>
          )}

          {res.available && res.recommendations.slice(0, showAllMap[res.path] ? undefined : 6).map((r: any) => (
            <div key={r.product_id} style={{ padding: "12px 0", borderBottom: "1px solid var(--ne-line, #333)" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                <strong>{r.name}</strong>
                <span className="portal-sub on-card" style={{ fontSize: 12 }}>{r.issuer}</span>
                <span className="portal-sub on-card" style={{ fontSize: 12, fontWeight: 700 }}>{TIER_LABEL[r.tier] || "Match"}</span>
                {typeof r.annual_fee === "number" && (
                  <span className="portal-sub on-card" style={{ fontSize: 12 }}>{r.annual_fee === 0 ? "No annual fee" : `$${r.annual_fee}/yr`}</span>
                )}
                <span style={{ flex: 1 }} />
                {r.apply_url && (
                  <a className="portal-btn-primary" href={r.apply_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                    Apply <ExternalLink size={12} aria-hidden="true" />
                  </a>
                )}
              </div>
              {r.prequal_url && (
                <p style={{ margin: "6px 0 0" }}>
                  <a className="portal-btn-ghost" href={r.prequal_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                    Check your rate first (soft pull — no score impact)
                  </a>
                </p>
              )}
              {r.highlights && <p className="portal-sub on-card" style={{ fontSize: 13, margin: "4px 0 0" }}>{r.highlights}</p>}
              {r.reasons.map((x: string, i: number) => (
                <p key={i} style={{ fontSize: 13, margin: "4px 0 0", color: "var(--ne-success)" }}>✓ {x}</p>
              ))}
              {r.warnings.map((x: string, i: number) => (
                <p key={i} style={{ fontSize: 13, margin: "4px 0 0", color: "var(--ne-warning-text, #d97706)" }}>⚠ {x}</p>
              ))}
            </div>
          ))}

          {res.available && res.recommendations.length > 6 && (
            <p style={{ marginTop: 10 }}>
              <button className="portal-btn-ghost" onClick={() => setShowAllMap((m) => ({ ...m, [res.path]: !m[res.path] }))}>
                {showAllMap[res.path] ? "Show fewer" : `Show all ${res.recommendations.length} matches`}
              </button>
            </p>
          )}
          {res.disclaimers?.length > 0 && (
            <p className="portal-sub on-card" style={{ fontSize: 12, marginTop: 10 }}>{res.disclaimers.join(" ")}</p>
          )}
        </article>
      ))}
    </PortalShell>
  );
}
